/**
 * clients listing.
 */
import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ClientService, Client, ClientAuthListOutput, CountryService, Country } from "../../../swaggerServices";
import { UserInfoService } from "../user-info.service";
import { Subscription } from "rxjs";
import { DropdownListOption } from "../../shared/components/search-dropdown/search-dropdown.component";
import { TaxYearsService } from "../../shared/util/tax-years.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfigStateService } from "../config/config-state.service";
import { Tenant } from "../../shared/types/tenant.enum";
import { TaxReturnTypeSubTypeService } from "../../shared/util/tax-return-type-subtype.service";
import { TenantConfigService } from "../../shared/services/tenant-config.service";
import { TranslocoService} from "@jsverse/transloco";
import { byId } from "../../shared/util/byId";

declare var $: any;

const filePattern = (/\.(csv|xlsx)$/i);

@Component({
    selector: 'app-clients',
    templateUrl: './clients.component.html',
    styleUrls: ['./clients.component.scss'],
    standalone: false
})
export class ClientsComponent implements OnInit, OnDestroy {
  clients: Client[]; // list of clients
  clientSearch: string; // search input
  newClientForm: UntypedFormGroup = new UntypedFormGroup({}); // form for new client creation
  clientName: string; // new client input
  clientNameInput: UntypedFormControl;
  clientDigitalGatewayIdentifier: number; // new client input
  clientDigitalGatewayIdentifierInput: UntypedFormControl;
  validateDGIText: string; //validate Number
  loading = false;
  isUploading: boolean;
  bulkAddAssignee: any;
  hasResults: boolean; // whether csv upload has assignee results
  partialSuccessUpload: boolean;
  @ViewChild('taxpayersUpload') taxpayersUpload: ElementRef;
  @ViewChild('newClientModal') newClientModalEl: ElementRef;
  @ViewChild('successModal') successModalEl: ElementRef;
  @ViewChild('taxpayersAuthListPreUpload') taxpayersAuthListPreUpload: ElementRef;
  @ViewChild("countryDropDown") countryDropDown: ElementRef;
  searchForm: UntypedFormGroup = new UntypedFormGroup({});
  taxYear: UntypedFormControl;
  taxYears: DropdownListOption[];
  subscriptions: Subscription[] = [];
  showUploadList: boolean;
  authListForUploadFile: File;
  authListUploadForm: UntypedFormGroup;
  taxReturnTypeCtrl: UntypedFormControl;
  taxReturnSubTypeCtrl: UntypedFormControl;
  taxReturnTypeOptions: DropdownListOption[];
  taxReturnSubTypeOptions: DropdownListOption[];
  taxReturnSubTypeOptionsCache: { [taxReturnTypeId: string]: DropdownListOption[] } = {};
  showTypeSubType = true;
  uiConfig: TenantConfigService;
  taxIdVariable: string;
  taxReturnAlias: string;
  taxpayerAlias: string;
  atlasIdVariable: string;
  entityPrimaryName: string;
  entitySecondaryName: string;
  yearLabel: string;
  countryAlias: string;

  allCountries: Country[] = [];
  allCountriesById = {};
  newClientCountryId: number = null;
  countryOptions: DropdownListOption[] = []
  defaultCountryId: number = null;
  showCountries: boolean = false;

  constructor(private zone: NgZone,
    private clientService: ClientService,
    public userInfo: UserInfoService,
    public taxYearsService: TaxYearsService,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private config: ConfigStateService,
    private taxReturnTypeSubTypeService: TaxReturnTypeSubTypeService,
    private translocoService: TranslocoService,
    private countryService: CountryService
  ) {
  }

  // get all clients and init form for new client creation
  ngOnInit() {
    this.clientNameInput = new UntypedFormControl("");
    this.newClientForm.addControl("clientName", this.clientNameInput);
    this.clientDigitalGatewayIdentifierInput = new UntypedFormControl("");
    this.newClientForm.addControl("clientDigitalGatewayIdentifier", this.clientDigitalGatewayIdentifierInput);
    this.taxYear = new UntypedFormControl();
    this.searchForm = new UntypedFormGroup({});
    this.searchForm.addControl("taxYear", this.taxYear);
    // NOTE(ajt): clients.component is a root-level component has a race condition with the config loading. Thus, 
    // we subscribe to the config's load event and update this property when the data is ready.
    this.subscriptions.push(this.config.loadConfig().subscribe((config) => {
      this.showUploadList = [Tenant.SG.toString()].includes(config.tenant);
    }));
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.taxpayerAlias = this.uiConfig.config.taxpayerAlias;
        this.entityPrimaryName = this.uiConfig.config.entityPrimaryName;
        this.entitySecondaryName = this.uiConfig.config.entityPrimaryName;
        this.atlasIdVariable = this.uiConfig.config.atlasId;
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.countryAlias = this.uiConfig.config.countryAlias
        this.translocoService.selectTranslate('year').subscribe(value => this.yearLabel = value);
        this.translocoService.selectTranslate('tax-payer-alias').subscribe(value => this.taxpayerAlias = value);
        this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
        this.translocoService.selectTranslate('country-alias').subscribe(value => this.countryAlias = value);
      })
    );

    // Ways for the tax year to change
    // Scenario #1: dropdown selected -> update form control -> update route
    // Scenario #2: browser navigation -> route updated -> update form control

    // navigate when the form control changes (i.e. dropdown selected, or set in response to the route changing)
    this.subscriptions.push(this.taxYear.valueChanges.subscribe((val: DropdownListOption) => {
      if (val && val.value) {
        // form control change always navigates
        this.router.navigate(['clients'], { queryParams: { taxYear: Number(val.value) } });
      }
    }));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
      if (this.taxYears == null) {
        return;
      }
      // Route change always triggers updating the rest of the page
      const newTaxYear = Number(params.taxYear);
      const opt = this.taxYears.find(({ value }) => Number(value) === newTaxYear);
      if (opt) {
        this.taxYear.setValue(opt);
        this.getClients(newTaxYear);
      }
    }));

    this.subscriptions.push(this.taxYearsService.getTaxYears().subscribe((years: DropdownListOption[]) => {
      this.taxYears = years;
      const taxYr: number = this.activatedRoute.snapshot.queryParams.taxYear || this.taxYearsService.getCurrentTaxYear();
      this.taxYear.setValue(taxYr);
      this.getClients(taxYr);
    }));

    this.subscriptions.push(this.countryService.getCountrys().subscribe((c) => {
      this.allCountries = c;
      this.allCountriesById = byId(c);
      this.showCountries = c?.length > 1;
    }));

    this.initAuthListUploadForm();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // gets all clients
  getClients(year: number) {
    this.loading = true;
    return this.clientService.getClients(year).subscribe((data) => {
      this.loading = false;
      this.clients = data;
    }, () => {
      this.loading = false;
    });
  }

  // shows new client form in modal
  openClientModal() {
    if (this.config.featureFlags.onlyAllowSingleCountryPerClient) {
      this.defaultCountryId = null;
      this.newClientCountryId = null;
      this.countryOptions = [];

      this.showCountries = this.userInfo.countries.length > 1;
      
      if (this.userInfo.countries.length === 1) { 
        this.defaultCountryId = this.userInfo.countries[0].id;
        this.newClientCountryId = this.userInfo.countries[0].id;
      }

      this.userInfo.countries.forEach((c) => {
        this.countryOptions.push({ id: c.id, name: c.countryCode, value: `${c.id}`, selected: c.id === this.defaultCountryId })
      });
    }

    this.clientName = "";
    this.clientDigitalGatewayIdentifier = null;
    $(this.newClientModalEl.nativeElement).modal('show');

    $(this.countryDropDown.nativeElement).dropdown({
      values: this.countryOptions,
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        if (value) {
          this.newClientCountryId = parseInt(value);
        }
      },
    });
  }

  // saves a client then show success modal and refresh clients list
  saveNewClient() {
    if (!this.clientName) return false;
    if(!this.validateDigitalGatewayIdentifier())return false;
    this.clientService.addClient({
      name: this.clientName,
      digitalGatewayIdentifier: this.clientDigitalGatewayIdentifier,
      countryId: this.config.featureFlags.onlyAllowSingleCountryPerClient? this.newClientCountryId : null
    }).subscribe(
      (data: any) => {
        this.getClients(this.taxYear.value.value);
        if (data.id) $(this.successModalEl.nativeElement).modal('show');
      });
  }
  validateDigitalGatewayIdentifier() {
    if (this.clientDigitalGatewayIdentifier > 1999999999) {
      this.validateDGIText = "You have exceeded the maximum value 1,999,999,999";
      return false;
    } else if (this.clientDigitalGatewayIdentifier < 0) {
      this.validateDGIText = "You are below the minimum value 0";
      return false;
    } else {
      this.validateDGIText = '';
      return true;
    }
  }
  initAuthListUploadForm() {
    this.taxReturnTypeCtrl = new UntypedFormControl('', {
      validators: [Validators.required]
    });
    this.taxReturnSubTypeCtrl = new UntypedFormControl('', {
      validators: [Validators.required]
    });
    this.authListUploadForm = new UntypedFormGroup({
      taxReturnType: this.taxReturnTypeCtrl,
      taxReturnSubType: this.taxReturnSubTypeCtrl,
    });

    this.taxReturnTypeCtrl.registerOnChange((a) => {
      if (!a) { return; }
      this.updateTaxReturnSubTypes();
    });
    this.subscriptions.push(this.taxReturnTypeSubTypeService.getTaxReturnTypes().subscribe((trt: any) => {
      this.taxReturnTypeOptions = trt;
      if (this.taxReturnTypeOptions.length === 1) {
        setTimeout(() => {
          this.taxReturnTypeCtrl.setValue(this.taxReturnTypeOptions[0]);
        }, 0);
      }
    }));

    this.taxReturnSubTypeOptions = [];
    this.resetAuthListUploadForm();
  }

  resetAuthListUploadForm() {
    this.authListUploadForm.reset();
  }

  updateTaxReturnSubTypes() {
    const taxReturnTypeId = this.taxReturnTypeCtrl.value.id;
    this.taxReturnSubTypeOptions = null;

    // check local cache first
    if (this.taxReturnSubTypeOptionsCache[taxReturnTypeId]) {
      // NOTE(ajt): timeout required so the subtype select component refreshes correctly
      setTimeout(() => {
        this.taxReturnSubTypeOptions = this.taxReturnSubTypeOptionsCache[taxReturnTypeId];
        if (this.taxReturnSubTypeOptions.length === 1) {
          if (this.taxReturnTypeOptions.length === 1) {
            this.showTypeSubType = false;
          }
          setTimeout(() => {
            this.taxReturnSubTypeCtrl.setValue(this.taxReturnSubTypeOptions[0]);
          }, 0);
        }
      }, 0);
      return;
    }

    this.subscriptions.push(this.taxReturnTypeSubTypeService.getTaxReturnSubTypesByType(taxReturnTypeId).subscribe((tst) => {
      this.taxReturnSubTypeOptionsCache[taxReturnTypeId] = tst;
      this.taxReturnSubTypeOptions = this.taxReturnSubTypeOptionsCache[taxReturnTypeId];
      if (this.taxReturnSubTypeOptions.length === 1) {
        if (this.taxReturnTypeOptions.length === 1) {
          this.showTypeSubType = false;
        }
        setTimeout(() => {
          this.taxReturnSubTypeCtrl.setValue(this.taxReturnSubTypeOptions[0]);
        }, 0);
      }
    }));
  }
  preStartBulkUpload(files: any) {
    this.resetAuthListUploadForm();

    const isValidType = filePattern.test(files[0].name);
    delete this.authListForUploadFile;
    delete this.bulkAddAssignee;
    delete this.hasResults;
    delete this.partialSuccessUpload;

    if (isValidType) {
      this.authListForUploadFile = files[0];
      if (this.showTypeSubType) {
        const el = $(this.taxpayersAuthListPreUpload.nativeElement);
        el.modal({
          closable: false,
          onApprove: () => {
            this.zone.run(() => {
              this.bulkUpload();
            });
            return false;
          },
        }).modal("show");
      } else {
        this.zone.run(() => {
          this.bulkUpload();
        });
      }
    } else {
      this.bulkAddAssignee = {
        error: {
          message: "Please upload a file in .csv or .xlsx format."
        }
      };
      $(this.taxpayersUpload.nativeElement).modal('show');
    }
  }

  // Authlist bulk upload, validates for csv file format
  bulkUpload() {
    if (!this.authListUploadForm.valid && this.showTypeSubType) {
      for (const control of Object.values(this.authListUploadForm.controls)) {
        control.markAsDirty();
      }
      // this.focusFirstInvalidInput();
      return;
    }

    if (!this.authListForUploadFile) { return; }
    delete this.bulkAddAssignee;
    delete this.hasResults;
    delete this.partialSuccessUpload;

    const year = this.taxYear.value.value;
    const taxReturnTypeId = this.showTypeSubType ? Number(this.taxReturnTypeCtrl.value.id) : Number(this.taxReturnTypeOptions[0].value);
    const taxReturnSubTypeId = this.showTypeSubType ? Number(this.taxReturnSubTypeCtrl.value.id) : Number(this.taxReturnSubTypeOptions[0].value);
    this.isUploading = true;
    if (this.showTypeSubType) {
      $(this.taxpayersAuthListPreUpload.nativeElement).modal('hide');
    }
    this.clientService.bulkAddAssigneeWithClient(this.authListForUploadFile, year, taxReturnTypeId, taxReturnSubTypeId).subscribe(
      (d: ClientAuthListOutput) => {

        this.bulkAddAssignee = d;

        // determine result message
        if (this.bulkAddAssignee.assigneesAdded.length > 0
          || this.bulkAddAssignee.assigneesUpdated.length > 0
          || this.bulkAddAssignee.assigneesMovedPop.length > 0
          || this.bulkAddAssignee.assigneesWithPopulationConflict.length > 0
          || this.bulkAddAssignee.assigneesToActionManually.length > 0
          || this.bulkAddAssignee.newTaxReturns.length > 0
          || this.bulkAddAssignee.updatedTaxReturns.length > 0
          || this.bulkAddAssignee.assigneesClientNotFound.length > 0
          || this.bulkAddAssignee.assigneesPopulationNotFound.length > 0
        ) {
          this.hasResults = true;

          if (this.bulkAddAssignee.assigneesWithPopulationConflict.length > 0
            || this.bulkAddAssignee.assigneesToActionManually.length > 0
          ) {
            this.partialSuccessUpload = true;
          }
        }

        $(this.taxpayersUpload.nativeElement).modal({ observeChanges: true }).modal('show');
        setTimeout(() => $('.ui.accordion').accordion());

        // this.populationRouteResolver.refreshPopulation(populationId);
        this.isUploading = false;

      },
      (e: Error) => {
        this.bulkAddAssignee = { error: { message: e.message } };
        $(this.taxpayersUpload.nativeElement).modal('show');
        this.isUploading = false;
      });
  }

  getPopulationCountryCode(countryId: number) {    
    return this.allCountriesById[countryId]?.countryCode || "";
  }
}
